import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function PriceBandwidthChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const dates = data.dates;
    const n = dates.length;

    // Collect all values for Y domain
    const allValues = [
      ...data.min,
      ...data.max,
      ...data.upper2,
      ...data.lower2,
    ].filter((v) => v != null);

    const priceMin = d3.min(allValues);
    const priceMax = d3.max(allValues);
    const pricePadding = (priceMax - priceMin) * 0.1 || 1;

    // Scales
    const x = d3.scalePoint()
      .domain(dates)
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Helper to build area data
    const buildAreaData = (lowerArr, upperArr) =>
      dates.map((date, i) => ({
        date,
        lower: lowerArr[i],
        upper: upperArr[i],
      }));

    const areaGen = d3.area()
      .x((d) => x(d.date))
      .y0((d) => y(d.lower))
      .y1((d) => y(d.upper));

    // Band 1: +/- 2 sigma (outermost, light blue)
    g.append('path')
      .datum(buildAreaData(data.lower2, data.upper2))
      .attr('d', areaGen)
      .attr('fill', '#2563eb')
      .attr('fill-opacity', 0.1)
      .attr('stroke', 'none');

    // Band 2: +/- 1 sigma (inner, medium blue)
    g.append('path')
      .datum(buildAreaData(data.lower1, data.upper1))
      .attr('d', areaGen)
      .attr('fill', '#2563eb')
      .attr('fill-opacity', 0.2)
      .attr('stroke', 'none');

    // Min line (light gray dashed)
    const minLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(data.min)
      .attr('d', minLine)
      .attr('fill', 'none')
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // Max line (light gray dashed)
    const maxLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(data.max)
      .attr('d', maxLine)
      .attr('fill', 'none')
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // Mean line (solid blue)
    const meanLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(data.mean)
      .attr('d', meanLine)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5);

    // Dots on mean line
    data.mean.forEach((v, i) => {
      if (v == null) return;
      g.append('circle')
        .attr('cx', x(dates[i]))
        .attr('cy', y(v))
        .attr('r', 3)
        .attr('fill', '#2563eb');
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((d) => formatDateLabel(d)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => `$${v}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Crosshair line (initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Invisible overlay columns for tooltip hover
    const colWidth = n > 1 ? innerW / (n - 1) : innerW;

    dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            mean: data.mean[i],
            upper1: data.upper1[i],
            lower1: data.lower1[i],
            upper2: data.upper2[i],
            lower2: data.lower2[i],
            min: data.min[i],
            max: data.max[i],
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Bandwidth</div>
          <div style={styles.subtitle}>Mean pest365 price +/- standard deviation bands over time</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No bandwidth data available
        </div>
      </div>
    );
  }

  const tooltipRows = tooltip
    ? [
        { label: 'Max', value: tooltip.max, color: '#9ca3af' },
        { label: '+2\u03c3', value: tooltip.upper2, color: '#93c5fd' },
        { label: '+1\u03c3', value: tooltip.upper1, color: '#60a5fa' },
        { label: 'Mean', value: tooltip.mean, color: '#2563eb' },
        { label: '-1\u03c3', value: tooltip.lower1, color: '#60a5fa' },
        { label: '-2\u03c3', value: tooltip.lower2, color: '#93c5fd' },
        { label: 'Min', value: tooltip.min, color: '#9ca3af' },
      ]
    : [];

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Bandwidth</div>
        <div style={styles.subtitle}>Mean pest365 price +/- standard deviation bands over time</div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2.5" />
          </svg>
          <span>Mean</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#2563eb" fillOpacity="0.2" rx="2" />
          </svg>
          <span>{'\u00b1'}1{'\u03c3'}</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#2563eb" fillOpacity="0.1" rx="2" />
          </svg>
          <span>{'\u00b1'}2{'\u03c3'}</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#d1d5db" strokeWidth="1" strokeDasharray="4,3" />
          </svg>
          <span>Min / Max</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltipRows.map((row) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: row.color, fontWeight: row.label === 'Mean' ? 600 : 400 }}>
                {row.label}
              </span>
              <span style={{ fontWeight: 600 }}>
                ${row.value != null ? row.value.toFixed(2) : '--'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
